/**
 * Apollo Cache Model Reducers
 *
 * These are the methods to update an item in the Apollo cache after a mutation occurs.
 */

import { ExecutionResult } from 'react-apollo';
import { ApolloQueryResult } from 'apollo-client';
import { DataProxy } from 'apollo-cache';
import { DocumentNode } from 'graphql';

import { Content, Feed, QueryGetFeedArgs, ReactionType, Comment, CommentsList } from 'api';

type ReducerArgs = (feedResult: Content, result: ExecutionResult['data']) => void;
type FeedResultArgs = (contentId: IReducer['contentId'], getFeed: Feed) => void;

interface IReducer {
  contentId: Content['content_id'];
  cache: DataProxy;
  result: ExecutionResult | ApolloQueryResult<{}>;
  query: DocumentNode;
  variables: QueryGetFeedArgs;
}

interface IReducers {
  createReaction: ReducerArgs;
  deleteReaction: ReducerArgs;
  createComment: ReducerArgs;
  deleteComment: ReducerArgs;
  getActiveComments: ReducerArgs;
  processContent: ReducerArgs;
}

// Reducers modify the result in-place
const reducers: IReducers = {
  createReaction: feedResult => {
    const { reactions } = feedResult;

    reactions.user_reactions = [ReactionType.Like];
    ++reactions.reaction_count.total;
  },
  deleteReaction: feedResult => {
    const { reactions } = feedResult;

    reactions.user_reactions = [];
    --reactions.reaction_count.total;
  },
  createComment: (feedResult, comment: Comment) => {
    const { comments } = feedResult;

    ++comments.count;
    comments.comments.push(comment);
  },
  deleteComment: (feedResult, comment: Comment) => {
    const { comments } = feedResult;
    const { id } = comment;
    const commentList = comments.comments;
    const index = commentList.findIndex(cachedComment => cachedComment.id === id);

    commentList.splice(index, 1);
    --comments.count;
  },
  getActiveComments: (feedResult, commentsObject: CommentsList) => {
    const { comments } = feedResult;
    const typeSafeComments = comments.comments as Comment[];

    Object.assign(comments, {
      id: commentsObject.id,
      created_at: commentsObject.created_at,
      paginate: commentsObject.paginate,
    });

    comments.comments = commentsObject.comments.concat(typeSafeComments);
  },
  processContent: (feedResult, result) => {
    Object.assign(feedResult, result);
  },
};

// Find the getFeed.result being modified
const getFeedResult: FeedResultArgs = (contentId, getFeed) => {
  return getFeed.results.find((feedResult: Content) => {
    return feedResult.content_id === contentId;
  });
};

/**
 * Four steps to update the cache
 *
 * 1. Read the cache using the current `query` and `variables`
 * 2. Find the target feed result using the `contentId`
 * 3. Modify the result in-place. Cloning isn't needed
 * 4. Write the result back to the cache
 */
export default (options: IReducer) => {
  const { contentId, cache, result, query, variables } = options;
  const { getContent, getFeed } = cache.readQuery({ query, variables });

  const queryResult = getContent || getFeedResult(contentId, getFeed);
  const mutations = Object.keys(result.data);

  mutations.forEach(mutation => {
    reducers[mutation](queryResult, result.data[mutation]);
  });

  cache.writeQuery({
    query,
    variables,
    data: { getFeed, getContent },
  });
};
